import axios from 'axios';
import { BASE_URL } from '../config';

import type { Risorsa, Progetto, Allocazione, AllocazioneInput, ProgettoInput, RisorsaInput } from '../types';
import type { GruppoProgetti, CreateGruppoProgettiDto, UpdateGruppoProgettiDto } from '../types/gruppo';

const API_BASE_URL = BASE_URL
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor per aggiungere l'id_utente dall'utente loggato
api.interceptors.request.use((config) => {
  const userStr = sessionStorage.getItem('user');
  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      if (user && user.id) {
        config.headers['x-user-id'] = user.id.toString();
      }
    } catch (e) {
      console.error('Error parsing user from sessionStorage', e);
    }
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Risorse
export const getRisorse = async (includeDisabled: boolean = false): Promise<Risorsa[]> => {
  const response = await api.get<Risorsa[]>('/risorse', {
    params: { includeDisabled: includeDisabled ? 'true' : 'false' }
  });
  return response.data;
};

export const getRisorsa = async (id: number): Promise<Risorsa> => {
  const response = await api.get<Risorsa>(`/risorse/${id}`);
  return response.data;
};

export const createRisorsa = async (risorsa: RisorsaInput): Promise<Risorsa> => {
  const response = await api.post<Risorsa>('/risorse', risorsa);
  return response.data;
};

export const updateRisorsa = async (id: number, risorsa: RisorsaInput): Promise<Risorsa> => {
  const response = await api.put<Risorsa>(`/risorse/${id}`, risorsa);
  return response.data;
};

export const deleteRisorsa = async (id: number): Promise<void> => {
  await api.delete(`/risorse/${id}`);
};

// Progetti
export interface ProgettiPaginatedResponse {
  data: Progetto[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
  };
}

export const getProgetti = async (
  includeDisabled: boolean = false,
  page: number = 1,
  limit: number = 50,
  search: string = ''
): Promise<ProgettiPaginatedResponse> => {
  const response = await api.get<ProgettiPaginatedResponse>('/progetti', {
    params: {
      includeDisabled: includeDisabled ? 'true' : 'false',
      page,
      limit,
      search
    }
  });
  return response.data;
};

export const getProgetto = async (id: number): Promise<Progetto> => {
  const response = await api.get<Progetto>(`/progetti/${id}`);
  return response.data;
};

export const createProgetto = async (progetto: ProgettoInput): Promise<Progetto> => {
  const response = await api.post<Progetto>('/progetti', progetto);
  return response.data;
};

export const updateProgetto = async (id: number, progetto: ProgettoInput): Promise<Progetto> => {
  const response = await api.put<Progetto>(`/progetti/${id}`, progetto);
  return response.data;
};

export const deleteProgetto = async (id: number): Promise<void> => {
  await api.delete(`/progetti/${id}`);
};

// Allocazioni
export const getAllocazioni = async (anno: number): Promise<Allocazione[]> => {
  const response = await api.get<Allocazione[]>(`/allocazioni/${anno}`);
  return response.data;
};

export const getAllocazioniRisorsa = async (
  anno: number,
  idRisorsa: number
): Promise<Allocazione[]> => {
  const response = await api.get<Allocazione[]>(`/allocazioni/${anno}/${idRisorsa}`);
  return response.data;
};

export const createOrUpdateAllocazione = async (
  allocazione: AllocazioneInput
): Promise<Allocazione> => {
  const response = await api.post<Allocazione>('/allocazioni', allocazione);
  return response.data;
};

export const updateAllocazione = async (
  id: number,
  percentuale: number
): Promise<Allocazione> => {
  const response = await api.put<Allocazione>(`/allocazioni/${id}`, { percentuale });
  return response.data;
};

export const deleteAllocazione = async (id: number): Promise<void> => {
  await api.delete(`/allocazioni/${id}`);
};

export const deleteAllocazioneByParams = async (
  id_risorsa: number,
  id_progetto: number,
  anno: number,
  mese: number
): Promise<void> => {
  await api.delete('/allocazioni/batch/delete', {
    data: { id_risorsa, id_progetto, anno, mese }
  });
};

// Sync Progetti HRP
export const testSyncConnection = async (): Promise<{ success: boolean; message: string }> => {
  const response = await api.get('/sync-progetti/test-connection');
  return response.data;
};

export const getSyncStats = async (): Promise<{ progetti_sincronizzati: number; ultima_modifica: string }> => {
  const response = await api.get('/sync-progetti/stats');
  return response.data;
};

export const getLastSync = async (): Promise<{ last_sync_timestamp: string; formatted: string }> => {
  const response = await api.get('/sync-progetti/last-sync');
  return response.data;
};

export const syncSingleProject = async (hrpId: number): Promise<any> => {
  const response = await api.post('/sync-progetti/sync-single', { hrp_id: hrpId });
  return response.data;
};

export const syncPreview = async (): Promise<any> => {
  const response = await api.post('/sync-progetti/preview', {});
  return response.data;
};

export const syncAllProjects = async (): Promise<any> => {
  const response = await api.post('/sync-progetti/sync-all', {});
  return response.data;
};

// Sync Clienti HRP
export const syncAllClienti = async (): Promise<any> => {
  const response = await api.post('/sync-clienti/sync-all', {});
  return response.data;
};

export const getSyncClientiStats = async (): Promise<{ clienti_sincronizzati: number; ultima_modifica: string }> => {
  const response = await api.get('/sync-clienti/stats');
  return response.data;
};

export const getSyncClientiLastSync = async (): Promise<{ last_sync_timestamp: string; formatted: string }> => {
  const response = await api.get('/sync-clienti/last-sync');
  return response.data;
};

// Sync Rendicontazioni HRP
export const testRendicontazioniConnection = async (): Promise<{ success: boolean; message: string }> => {
  const response = await api.get('/sync-rendicontazioni/test-connection');
  return response.data;
};

export const getRendicontazioniStats = async (): Promise<{ allocazioni_da_hrp: number; risorse_con_rendicontazioni: number; ultima_sincronizzazione: string }> => {
  const response = await api.get('/sync-rendicontazioni/stats');
  return response.data;
};

export const getRendicontazioniLastSync = async (): Promise<{ last_sync_timestamp: string; formatted: string }> => {
  const response = await api.get('/sync-rendicontazioni/last-sync');
  return response.data;
};

export const updateRendicontazioniLastSync = async (timestamp: string): Promise<{ success: boolean; message: string; last_sync_timestamp: string; formatted: string }> => {
  const response = await api.put('/sync-rendicontazioni/last-sync', { timestamp });
  return response.data;
};

export const rendicontazioniPreview = async (risorseIds?: number[]): Promise<any> => {
  const response = await api.post('/sync-rendicontazioni/preview', { risorse_ids: risorseIds });
  return response.data;
};

export const syncAllRendicontazioni = async (risorseIds?: number[]): Promise<any> => {
  const response = await api.post('/sync-rendicontazioni/sync-all', { risorse_ids: risorseIds });
  return response.data;
};

// Gruppi Progetti
export const getGruppiProgetti = async (): Promise<GruppoProgetti[]> => {
  const response = await api.get<GruppoProgetti[]>('/gruppi-progetti');
  return response.data;
};

export const getGruppoProgetti = async (id: number): Promise<GruppoProgetti> => {
  const response = await api.get<GruppoProgetti>(`/gruppi-progetti/${id}`);
  return response.data;
};

export const createGruppoProgetti = async (gruppo: CreateGruppoProgettiDto): Promise<GruppoProgetti> => {
  const response = await api.post<GruppoProgetti>('/gruppi-progetti', gruppo);
  return response.data;
};

export const updateGruppoProgetti = async (id: number, gruppo: UpdateGruppoProgettiDto): Promise<GruppoProgetti> => {
  const response = await api.put<GruppoProgetti>(`/gruppi-progetti/${id}`, gruppo);
  return response.data;
};

export const deleteGruppoProgetti = async (id: number): Promise<void> => {
  await api.delete(`/gruppi-progetti/${id}`);
};

export const addProgettoToGruppo = async (gruppoId: number, progettoId: number): Promise<void> => {
  await api.post(`/gruppi-progetti/${gruppoId}/progetti`, { progetto_id: progettoId });
};

export const removeProgettoFromGruppo = async (gruppoId: number, progettoId: number): Promise<void> => {
  await api.delete(`/gruppi-progetti/${gruppoId}/progetti/${progettoId}`);
};

// Capacity Planning V2
export interface CapacityPlanningV2MonthData {
  mese: number;
  capacita_disponibile: number;
  demand_teorico: number;
  supply_reale: number;
  gap: number;
  saturazione: number;
  fte_mancanti: number;
  status: 'ok' | 'warning' | 'critical';
}

export interface CapacityPlanningV2Response {
  anno: number;
  num_risorse: number;
  giorni_lavorativi_mese: number;
  capacita_totale_mensile: number;
  summary: {
    total_demand: number;
    total_supply: number;
    total_gap: number;
    avg_saturazione: number;
    mesi_critici: number;
    fte_medio_mancante: number;
  };
  monthly_data: CapacityPlanningV2MonthData[];
}

export const getCapacityPlanningV2 = async (anno: number, strutture?: string[]): Promise<CapacityPlanningV2Response> => {
  const params = strutture && strutture.length > 0 ? { strutture: strutture.join(',') } : {};
  const response = await api.get<CapacityPlanningV2Response>(`/capacity-planning-v2/${anno}`, { params });
  return response.data;
};

// Strutture
export interface Struttura {
  nome: string;
  id_responsabile?: number;
  descrizione?: string;
  created_at?: string;
  updated_at?: string;
  responsabile_username?: string;
  responsabile_nome?: string;
  responsabile_cognome?: string;
}

export const getStrutture = async (): Promise<Struttura[]> => {
  const response = await api.get<Struttura[]>('/strutture');
  return response.data;
};

// Capacity Planning V2 - Drill Down
export interface CapacityPlanningV2Allocazione {
  risorsa_id: number;
  risorsa_nome: string;
  risorsa_cognome: string;
  risorsa_struttura: string;
  percentuale: number;
  giorni: number;
}

export interface CapacityPlanningV2ProgettoDettaglio {
  progetto_id: number;
  progetto_titolo: string;
  budget_totale: number;
  durata_mesi: number;
  demand_mensile: number;
  supply_mensile: number;
  gap: number;
  allocazioni: CapacityPlanningV2Allocazione[];
}

export interface CapacityPlanningV2DettaglioResponse {
  anno: number;
  mese: number;
  summary: {
    total_demand: number;
    total_supply: number;
    total_gap: number;
    num_progetti: number;
  };
  progetti: CapacityPlanningV2ProgettoDettaglio[];
}

export const getCapacityPlanningV2Dettaglio = async (
  anno: number,
  mese: number,
  strutture?: string[]
): Promise<CapacityPlanningV2DettaglioResponse> => {
  const params = strutture && strutture.length > 0 ? { strutture: strutture.join(',') } : {};
  const response = await api.get<CapacityPlanningV2DettaglioResponse>(
    `/capacity-planning-v2/${anno}/${mese}/dettaglio`,
    { params }
  );
  return response.data;
};

export default api;
